




































































import { computed, defineComponent, ref } from "@vue/composition-api";
import { TagInfo, TagInfoType } from "@scrile/api-provider/dist/api/TagsProvider";
import { UserLocation } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import providers from "@/lib/api";
import historyManager from "@/lib/historyManager";
import { $t } from "@/lib/i18nHelpers";
import { getRecentlyActiveTime } from "@/lib/time";
import config from "@/config";
import { Tab } from "@/types";
import { ROLES } from "@/const";
import BaseIcon from "@/components/BaseIcon";
import BaseInput from "@/components/BaseInput";
import BasePage from "@/components/BasePage";
import BaseTabs from "@/components/BaseTabs";
import FiltersCreatorModal from "@/components/FiltersCreatorModal";
import PageTitle from "@/components/PageTitle";
import usersRecentlyActiveController from "@/views/PageCreatorsList/controllers/usersRecentlyActiveController";
import CreatorsList from "./components/CreatorsList";
import CreatorsListTabs from "./components/CreatorsListTabs";
import usersAllController from "./controllers/usersAllController";
import usersLiveController from "./controllers/usersLiveController";
import usersOnlineController from "./controllers/usersOnlineController";
import usersSearchController from "./controllers/usersSearchController";
import { CreatorsTabsEnum } from "./types";

export default defineComponent({
  name: "PageCreatorsList",
  components: {
    BaseTabs,
    FiltersCreatorModal,
    BasePage,
    PageTitle,
    BaseIcon,
    BaseInput,
    CreatorsList,
    CreatorsListTabs,
  },
  props: {
    tab: { type: String as () => CreatorsTabsEnum, default: CreatorsTabsEnum.ALL },
  },
  setup(props) {
    const loading = ref(false);
    const showSelectTags = ref(false);
    const selectedTags = ref<TagInfo[]>([]);
    const allowSearch = computed(() => config.featureSearchBar);
    const showFilterBtn = computed(() => config.featureSearchBar && config.featureHashtags);
    const searchValue = ref("");
    const total = ref<Record<CreatorsTabsEnum, number>>({
      [CreatorsTabsEnum.ALL]: 0,
      [CreatorsTabsEnum.ONLINE]: 0,
      [CreatorsTabsEnum.LIVE]: 0,
      [CreatorsTabsEnum.RECENTLY_ACTIVE]: 0,
    });
    const languages = ref<string[]>([]);
    const locations = ref<UserLocation[]>([]);
    const selectedRole = ref(ROLES.CREATOR);

    const searchMode = computed(
      () =>
        !!searchValue.value || selectedTags.value.length > 0 || locations.value.length > 0 || languages.value.length > 0
    );
    const hasHistory = computed(() => historyManager.hasHistory());
    const selectedTagsIds = computed(() => selectedTags.value.map(tag => tag.id));
    const controller = computed(() => {
      if (searchMode.value) return usersSearchController;

      switch (props.tab) {
        case CreatorsTabsEnum.ONLINE:
          return usersOnlineController;
        case CreatorsTabsEnum.LIVE:
          return usersLiveController;
        case CreatorsTabsEnum.ALL:
          return usersAllController;
        case CreatorsTabsEnum.RECENTLY_ACTIVE:
          return usersRecentlyActiveController;
        default:
          return null;
      }
    });

    const roleTabs: Tab[] = [
      {
        text: $t("Creators"),
        value: ROLES.CREATOR,
      },
      {
        text: $t("Non-creators"),
        value: ROLES.MEMBER,
      },
    ];

    const onInputSearch = (value: string = "") => {
      searchValue.value = value;
    };

    const updateTotal = (activeTab: CreatorsTabsEnum, totalRecords: number | undefined) => {
      if (totalRecords === undefined) return;
      total.value = { ...total.value, [activeTab]: totalRecords };
    };

    const selectLanguages = (value: string[]) => {
      showSelectTags.value = false;
      languages.value = value;
    };

    const selectLocations = (value: UserLocation[]) => {
      showSelectTags.value = false;
      locations.value = value;
    };

    const getTotal = async () => {
      await Promise.all([
        providers.UserPublicDataProvider.search({
          data: { size: 1, role: selectedRole.value, disabled: false },
        }).then(r => (total.value[CreatorsTabsEnum.ALL] = r.totalRecords)),
        providers.UserPublicDataProvider.search({
          data: { size: 1, role: selectedRole.value, disabled: false, isOnline: true },
        }).then(r => (total.value[CreatorsTabsEnum.ONLINE] = r.totalRecords)),
        providers.UserPublicDataProvider.search({
          data: { size: 1, role: selectedRole.value, disabled: false, isLive: true },
        }).then(r => (total.value[CreatorsTabsEnum.LIVE] = r.totalRecords)),
        providers.UserPublicDataProvider.search({
          data: {
            size: 1,
            role: selectedRole.value,
            disabled: false,
            lastSeenAfter: getRecentlyActiveTime().toISOString(),
          },
        }).then(r => (total.value[CreatorsTabsEnum.RECENTLY_ACTIVE] = r.totalRecords)),
      ]);
    };

    const onTagsSelect = (tags: TagInfo[]) => {
      showSelectTags.value = false;
      selectedTags.value = tags;
    };

    getTotal();

    return {
      loading,
      allowSearch,
      searchValue,
      searchMode,
      hasHistory,
      controller,
      total,
      showSelectTags,
      selectedTags,
      showFilterBtn,
      TagInfoType,
      selectedTagsIds,
      languages,
      locations,
      roleTabs,
      selectedRole,
      onTagsSelect,
      onInputSearch,
      updateTotal,
      selectLanguages,
      selectLocations,
    };
  },
});
